import { FullStory, init } from '@fullstory/browser';

/**
 * Fullstory Service for Analytics and Session Recording
 * Provides comprehensive integration with Fullstory Browser API v2
 */
class FullstoryService {
  constructor() {
    this.isInitialized = false;
    this.orgId = process.env.REACT_APP_FULLSTORY_ORG_ID;
    this.debug = process.env.NODE_ENV === 'development';
  }

  /**
   * Initialize Fullstory with configuration
   */
  async initialize() {
    if (!this.orgId) {
      console.warn('Fullstory: REACT_APP_FULLSTORY_ORG_ID not configured');
      return false;
    }

    try {
      // Initialize Fullstory
      init({
        orgId: this.orgId,
        devMode: this.debug,
      });

      this.isInitialized = true;

      // Set up observers for capture events
      this.setupObservers();

      // Set up rage click handling
      this.setupRageClickHandling();

      if (this.debug) {
        console.log('Fullstory: Initialized successfully');
      }

      return true;
    } catch (error) {
      console.error('Fullstory: Initialization failed', error);
      return false;
    }
  }

  /**
   * Set up Fullstory event observers
   */
  setupObservers() {
    if (!this.isInitialized) return;

    // Observe when capture starts
    FullStory('observe', {
      type: 'start',
      callback: () => {
        if (this.debug) {
          console.log('Fullstory: Capture started');
        }
        this.onCaptureStarted();
      }
    });

    // Observe when capture shuts down
    FullStory('observe', {
      type: 'shutdown',
      callback: () => {
        if (this.debug) {
          console.log('Fullstory: Capture shutdown');
        }
      }
    });
  }

  /**
   * Handle capture started event
   */
  onCaptureStarted() {
    // Get session URL for debugging
    if (this.debug) {
      const sessionUrl = FullStory('getSession');
      console.log('Fullstory: Session URL', sessionUrl);
    }

    // Track initial page view
    this.trackPageView(window.location.pathname);
  }

  /**
   * Set up rage click event handling
   */
  setupRageClickHandling() {
    // Listen for rage click events
    window.addEventListener('fullstory/rageclick', (event) => {
      this.handleRageClick(event);
    });
  }

  /**
   * Handle rage click events
   */
  handleRageClick(event) {
    const { detail } = event;

    if (this.debug) {
      console.log('Fullstory: Rage click detected', detail);
    }

    // Track rage click as custom event
    this.trackEvent('Rage Click', {
      element: event.target?.tagName || 'unknown',
      className: event.target?.className || 'unknown',
      sessionUrl: detail?.eventReplayUrlAtCurrentTime,
      timestamp: detail?.eventStartTimeStamp,
    });

    // You can add additional handling here, like:
    // - Send notification to support team
    // - Trigger help widget
    // - Log to your own analytics
  }

  /**
   * Identify a user in Fullstory
   * @param {Object} userData - User data object
   * @param {string} userData.uid - Unique user identifier
   * @param {string} userData.email - User email
   * @param {string} userData.displayName - Display name
   * @param {Object} userData.properties - Additional user properties
   */
  identifyUser({ uid, email, displayName, ...properties }) {
    if (!this.isInitialized) return;

    try {
      const userProperties = {
        ...properties,
      };

      // Add special fields
      if (displayName) userProperties.displayName = displayName;
      if (email) userProperties.email = email;

      FullStory('setIdentity', {
        uid: uid.toString(),
        properties: userProperties,
      });

      if (this.debug) {
        console.log('Fullstory: User identified', { uid, displayName, email });
      }
    } catch (error) {
      console.error('Fullstory: Error identifying user', error);
    }
  }

  /**
   * Anonymize the current user
   */
  anonymizeUser() {
    if (!this.isInitialized) return;

    try {
      FullStory('setIdentity', { anonymous: true });

      if (this.debug) {
        console.log('Fullstory: User anonymized');
      }
    } catch (error) {
      console.error('Fullstory: Error anonymizing user', error);
    }
  }

  /**
   * Set user properties
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (!this.isInitialized) return;

    try {
      FullStory('setProperties', {
        type: 'user',
        properties,
      });

      if (this.debug) {
        console.log('Fullstory: User properties set', properties);
      }
    } catch (error) {
      console.error('Fullstory: Error setting user properties', error);
    }
  }

  /**
   * Track custom events
   * @param {string} eventName - Name of the event
   * @param {Object} properties - Event properties
   * @param {Object} schema - Optional schema for type definitions
   */
  trackEvent(eventName, properties = {}, schema = null) {
    if (!this.isInitialized) return;

    try {
      const eventData = {
        name: eventName,
        properties,
      };

      if (schema) {
        eventData.schema = schema;
      }

      FullStory('trackEvent', eventData);

      if (this.debug) {
        console.log('Fullstory: Event tracked', eventName, properties);
      }
    } catch (error) {
      console.error('Fullstory: Error tracking event', error);
    }
  }

  /**
   * Track page views and set page properties
   * @param {string} pageName - Page name
   * @param {Object} properties - Page properties
   */
  trackPageView(pageName, properties = {}) {
    if (!this.isInitialized) return;

    try {
      FullStory('setProperties', {
        type: 'page',
        properties: {
          pageName,
          ...properties,
          url: window.location.href,
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        },
      });

      // Also track as a custom event
      this.trackEvent('Page View', {
        page: pageName,
        path: window.location.pathname,
        ...properties,
      });

      if (this.debug) {
        console.log('Fullstory: Page view tracked', pageName);
      }
    } catch (error) {
      console.error('Fullstory: Error tracking page view', error);
    }
  }

  /**
   * Track user login
   * @param {Object} userData - User login data
   */
  trackLogin(userData) {
    this.trackEvent('User Login', {
      method: userData.method || 'unknown',
      role: userData.role,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track user logout
   */
  trackLogout() {
    this.trackEvent('User Logout', {
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track user registration
   * @param {Object} userData - User registration data
   */
  trackRegistration(userData) {
    this.trackEvent('User Registration', {
      method: userData.method || 'unknown',
      role: userData.role,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track errors
   * @param {Error|string} error - Error object or message
   * @param {Object} context - Additional context
   */
  trackError(error, context = {}) {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;

    this.trackEvent('Error', {
      message: errorMessage,
      stack: errorStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track feature usage
   * @param {string} featureName - Name of the feature
   * @param {Object} properties - Feature usage properties
   */
  trackFeatureUsage(featureName, properties = {}) {
    this.trackEvent('Feature Used', {
      feature: featureName,
      ...properties,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track business metrics
   * @param {string} metricName - Name of the metric
   * @param {Object} properties - Metric properties
   */
  trackBusinessMetric(metricName, properties = {}) {
    this.trackEvent('Business Metric', {
      metric: metricName,
      ...properties,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Get current session URL
   * @param {string} format - URL format ('url', 'url.now', 'id')
   * @returns {Promise<string|null>} Session URL or ID
   */
  async getSessionUrl(format = 'url') {
    if (!this.isInitialized) return null;

    try {
      const sessionUrl = await FullStory('getSessionAsync', { format });
      return sessionUrl;
    } catch (error) {
      console.error('Fullstory: Error getting session URL', error);
      return null;
    }
  }

  /**
   * Control data capture
   * @param {boolean} enable - Whether to enable capture
   */
  setDataCapture(enable) {
    if (!this.isInitialized) return;

    try {
      if (enable) {
        FullStory('restart');
      } else {
        FullStory('shutdown');
      }

      if (this.debug) {
        console.log('Fullstory: Data capture', enable ? 'enabled' : 'disabled');
      }
    } catch (error) {
      console.error('Fullstory: Error controlling data capture', error);
    }
  }

  /**
   * Set user consent for data capture
   * @param {boolean} consent - Whether user has consented
   */
  setConsent(consent) {
    if (!this.isInitialized) return;

    try {
      FullStory('setIdentity', { consent });

      if (this.debug) {
        console.log('Fullstory: Consent set to', consent);
      }
    } catch (error) {
      console.error('Fullstory: Error setting consent', error);
    }
  }

  /**
   * Log messages to Fullstory console (without affecting browser console)
   * @param {string} level - Log level ('log', 'info', 'warn', 'error')
   * @param {string} message - Log message
   */
  log(level, message) {
    if (!this.isInitialized) return;

    try {
      FullStory('log', { level, msg: message });
    } catch (error) {
      console.error('Fullstory: Error logging message', error);
    }
  }

  /**
   * Check if Fullstory is initialized and ready
   * @returns {boolean} Whether Fullstory is ready
   */
  isReady() {
    return this.isInitialized;
  }
}

// Create singleton instance
const fullstoryService = new FullstoryService();

export default fullstoryService;
